// File: src/components/admin/analysis-tasks/AudienceRuleBuilder.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Visual audience rule builder for Analysis Tasks (Issue #67)
// Replaces the raw JSON textarea with a form-based UI for building
// audience targeting rules with dropdowns, typed inputs, and live preview.

import React, { useState, useMemo, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, IconButton, Chip, Alert, Collapse,
    TextField, MenuItem, FormControlLabel, Switch, FormGroup, Checkbox,
    Grid, Divider, ListSubheader, Select, FormControl, InputLabel, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import JsonViewTab from '../common/JsonViewTab';

// ─── Field Metadata ─────────────────────────────────────────────────────────

const FIELD_METADATA = {
    'user.daysSinceLastLogin':          { label: 'Days Since Last Login',     type: 'number',  category: 'user' },
    'user.campaignCount':               { label: 'Campaign Count',            type: 'number',  category: 'user' },
    'user.keywordCount':                { label: 'Keyword Count',             type: 'number',  category: 'user' },
    'user.adGroupCount':                { label: 'Ad Group Count',            type: 'number',  category: 'user' },
    'subscription.usagePercent':        { label: 'Usage Percentage',          type: 'number',  category: 'subscription' },
    'subscription.trialDaysRemaining':  { label: 'Trial Days Remaining',     type: 'number',  category: 'subscription' },
    'subscription.isActive':            { label: 'Subscription Active',       type: 'boolean', category: 'subscription' },
    'platform.isConnected':             { label: 'Platform Connected',        type: 'boolean', category: 'platform' },
    'task.daysSinceLastRun':            { label: 'Days Since Last Run',       type: 'number',  category: 'task' },
    'task.consecutiveIgnored':          { label: 'Consecutive Ignored',       type: 'number',  category: 'task' },
};

const CATEGORY_LABELS = {
    user:         'User',
    subscription: 'Subscription',
    platform:     'Platform',
    task:         'Task',
};

const CATEGORY_ORDER = ['user', 'subscription', 'platform', 'task'];

const OPERATOR_LABELS = {
    '>':       'Greater than',
    '<':       'Less than',
    '=':       'Equals',
    '>=':      'At least',
    '<=':      'At most',
    'between': 'Between',
    'is':      'Is',
    'is_not':  'Is not',
    'true':    'Is true',
    'false':   'Is false',
};

const NUMBER_OPERATORS = ['>', '<', '=', '>=', '<=', 'between', 'is', 'is_not'];
const BOOLEAN_OPERATORS = ['true', 'false'];

function getOperatorsForField(fieldName) {
    const meta = FIELD_METADATA[fieldName];
    if (!meta) return Object.keys(OPERATOR_LABELS);
    if (meta.type === 'boolean') return BOOLEAN_OPERATORS;
    if (meta.type === 'number') return NUMBER_OPERATORS;
    return Object.keys(OPERATOR_LABELS);
}

function isValuelessOperator(op) {
    return op === 'true' || op === 'false';
}

// ─── Grouped field options for the Select ────────────────────────────────────

function getGroupedFieldOptions() {
    const groups = [];
    for (const cat of CATEGORY_ORDER) {
        const fields = Object.entries(FIELD_METADATA).filter(([, m]) => m.category === cat);
        if (fields.length > 0) {
            groups.push({ category: cat, fields });
        }
    }
    return groups;
}

// ─── Validation helpers ──────────────────────────────────────────────────────

function validateCondition(cond, groupIndex, condIndex, allConditions) {
    const errors = [];
    if (!cond.field) {
        errors.push({ key: `${groupIndex}-${condIndex}-field`, message: 'Please select a field' });
    }
    if (!cond.operator) {
        errors.push({ key: `${groupIndex}-${condIndex}-operator`, message: 'Please select an operator' });
    }
    if (cond.operator && !isValuelessOperator(cond.operator)) {
        if (cond.operator === 'between') {
            const val = cond.value;
            if (!Array.isArray(val) || val.length !== 2) {
                errors.push({ key: `${groupIndex}-${condIndex}-value`, message: 'Between requires min and max values' });
            } else if (val[0] !== '' && val[1] !== '' && Number(val[0]) >= Number(val[1])) {
                errors.push({ key: `${groupIndex}-${condIndex}-value`, message: 'Min must be less than Max' });
            } else if (val[0] === '' || val[0] === undefined || val[1] === '' || val[1] === undefined) {
                errors.push({ key: `${groupIndex}-${condIndex}-value`, message: 'Please enter both min and max values' });
            }
        } else if (cond.value === '' || cond.value === undefined || cond.value === null) {
            errors.push({ key: `${groupIndex}-${condIndex}-value`, message: 'Please enter a value' });
        }
    }
    // Duplicate check
    if (cond.field && cond.operator) {
        const isDup = allConditions.some((other, idx) =>
            idx !== condIndex && other.field === cond.field && other.operator === cond.operator
        );
        if (isDup) {
            errors.push({ key: `${groupIndex}-${condIndex}-dup`, message: 'Duplicate condition in this group', severity: 'warning' });
        }
    }
    return errors;
}

function validateAllRules(rules) {
    const allErrors = [];
    (rules || []).forEach((group, gi) => {
        (group.conditions || []).forEach((cond, ci) => {
            allErrors.push(...validateCondition(cond, gi, ci, group.conditions));
        });
    });
    return allErrors;
}

// ─── Summary generator ───────────────────────────────────────────────────────

function generateSummaryText(rules) {
    if (!rules || rules.length === 0) return 'All users';

    const groupTexts = rules.map((group) => {
        if (!group.conditions || group.conditions.length === 0) return null;

        const condTexts = group.conditions.map((cond) => {
            const meta = FIELD_METADATA[cond.field];
            const fieldLabel = meta?.label || cond.field || '?';
            const opLabel = OPERATOR_LABELS[cond.operator] || cond.operator || '?';

            if (isValuelessOperator(cond.operator)) {
                return `${fieldLabel} ${opLabel}`;
            }
            if (cond.operator === 'between' && Array.isArray(cond.value)) {
                return `${fieldLabel} ${opLabel} ${cond.value[0] ?? '?'} and ${cond.value[1] ?? '?'}`;
            }
            return `${fieldLabel} ${opLabel} ${cond.value ?? '?'}`;
        });

        if (condTexts.length === 0) return null;
        const logic = group.logic || 'AND';
        const joined = condTexts.join(` ${logic} `);
        return condTexts.length > 1 ? `(${joined})` : joined;
    }).filter(Boolean);

    if (groupTexts.length === 0) return 'All users';
    return `Users where ${groupTexts.join(' AND ')}`;
}

// ─── ConditionRow ────────────────────────────────────────────────────────────

const ConditionRow = ({ condition, groupIndex, condIndex, onChange, onDelete, validationErrors }) => {
    const groupedFields = useMemo(getGroupedFieldOptions, []);
    const operators = useMemo(() => getOperatorsForField(condition.field), [condition.field]);
    const condErrors = validationErrors.filter(e => e.key.startsWith(`${groupIndex}-${condIndex}`));

    const handleFieldChange = (e) => {
        const newField = e.target.value;
        const meta = FIELD_METADATA[newField];
        // Reset operator and value when field changes type
        let newOp = condition.operator;
        let newVal = condition.value;
        if (meta?.type === 'boolean' && !BOOLEAN_OPERATORS.includes(newOp)) {
            newOp = 'true';
            newVal = undefined;
        } else if (meta?.type === 'number' && BOOLEAN_OPERATORS.includes(newOp)) {
            newOp = '>';
            newVal = '';
        }
        onChange({ ...condition, field: newField, operator: newOp, value: newVal });
    };

    const handleOperatorChange = (e) => {
        const newOp = e.target.value;
        let newVal = condition.value;
        if (isValuelessOperator(newOp)) {
            newVal = undefined;
        } else if (newOp === 'between' && !Array.isArray(newVal)) {
            newVal = ['', ''];
        } else if (newOp !== 'between' && Array.isArray(newVal)) {
            newVal = '';
        }
        onChange({ ...condition, operator: newOp, value: newVal });
    };

    const handleValueChange = (val) => {
        onChange({ ...condition, value: val });
    };

    const fieldError = condErrors.find(e => e.key.endsWith('-field'));
    const valueError = condErrors.find(e => e.key.endsWith('-value'));
    const dupWarning = condErrors.find(e => e.key.endsWith('-dup'));

    return (
        <Box
            data-testid={`condition-${groupIndex}-${condIndex}`}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
        >
            {/* Field Select */}
            <FormControl
                size="small"
                sx={{ minWidth: 220, flex: '1 1 220px' }}
                error={!!fieldError}
                data-testid={`condition-field-${groupIndex}-${condIndex}`}
            >
                <InputLabel>Field</InputLabel>
                <Select
                    value={condition.field || ''}
                    onChange={handleFieldChange}
                    label="Field"
                >
                    {groupedFields.map(({ category, fields }) => [
                        <ListSubheader key={`header-${category}`} sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'background.default' }}>
                            {CATEGORY_LABELS[category]}
                        </ListSubheader>,
                        ...fields.map(([fieldKey, meta]) => (
                            <MenuItem key={fieldKey} value={fieldKey}>
                                <Box>
                                    <Typography variant="body2">{meta.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">{fieldKey}</Typography>
                                </Box>
                            </MenuItem>
                        )),
                    ])}
                </Select>
                {fieldError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{fieldError.message}</Typography>}
            </FormControl>

            {/* Operator Select */}
            <FormControl
                size="small"
                sx={{ minWidth: 160, flex: '0 1 160px' }}
                data-testid={`condition-operator-${groupIndex}-${condIndex}`}
            >
                <InputLabel>Operator</InputLabel>
                <Select
                    value={condition.operator || ''}
                    onChange={handleOperatorChange}
                    label="Operator"
                >
                    {operators.map(op => (
                        <MenuItem key={op} value={op}>{OPERATOR_LABELS[op]}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Value Input */}
            <Box
                sx={{ flex: '1 1 160px', minWidth: 120 }}
                data-testid={`condition-value-${groupIndex}-${condIndex}`}
            >
                {isValuelessOperator(condition.operator) ? (
                    <TextField
                        size="small"
                        fullWidth
                        disabled
                        value="(no value needed)"
                        InputProps={{ readOnly: true }}
                    />
                ) : condition.operator === 'between' ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Min"
                            value={Array.isArray(condition.value) ? condition.value[0] : ''}
                            onChange={(e) => {
                                const arr = Array.isArray(condition.value) ? [...condition.value] : ['', ''];
                                arr[0] = e.target.value;
                                handleValueChange(arr);
                            }}
                            error={!!valueError}
                            sx={{ flex: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">-</Typography>
                        <TextField
                            size="small"
                            type="number"
                            label="Max"
                            value={Array.isArray(condition.value) ? condition.value[1] : ''}
                            onChange={(e) => {
                                const arr = Array.isArray(condition.value) ? [...condition.value] : ['', ''];
                                arr[1] = e.target.value;
                                handleValueChange(arr);
                            }}
                            error={!!valueError}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                ) : FIELD_METADATA[condition.field]?.type === 'number' ? (
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Value"
                        value={condition.value ?? ''}
                        onChange={(e) => handleValueChange(e.target.value === '' ? '' : Number(e.target.value))}
                        error={!!valueError}
                    />
                ) : (
                    <TextField
                        size="small"
                        fullWidth
                        label="Value"
                        value={condition.value ?? ''}
                        onChange={(e) => handleValueChange(e.target.value)}
                        error={!!valueError}
                    />
                )}
                {valueError && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{valueError.message}</Typography>}
            </Box>

            {/* Delete button */}
            <IconButton
                size="small"
                onClick={onDelete}
                color="error"
                sx={{ mt: 0.5 }}
                data-testid={`delete-condition-${groupIndex}-${condIndex}`}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>

            {/* Duplicate warning */}
            {dupWarning && (
                <Typography variant="caption" color="warning.main" sx={{ width: '100%' }}>
                    {dupWarning.message}
                </Typography>
            )}
        </Box>
    );
};

// ─── RuleGroup ───────────────────────────────────────────────────────────────

const RuleGroup = ({ group, groupIndex, onChange, onDelete, validationErrors }) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleLogicToggle = () => {
        onChange({ ...group, logic: group.logic === 'AND' ? 'OR' : 'AND' });
    };

    const handleConditionChange = (condIndex, updatedCond) => {
        const newConditions = [...(group.conditions || [])];
        newConditions[condIndex] = updatedCond;
        onChange({ ...group, conditions: newConditions });
    };

    const handleDeleteCondition = (condIndex) => {
        const newConditions = (group.conditions || []).filter((_, i) => i !== condIndex);
        onChange({ ...group, conditions: newConditions });
    };

    const handleAddCondition = () => {
        const newConditions = [...(group.conditions || []), { field: '', operator: '', value: '' }];
        onChange({ ...group, conditions: newConditions });
    };

    const handleDeleteGroup = () => {
        if ((group.conditions || []).length > 0) {
            setDeleteDialogOpen(true);
        } else {
            onDelete();
        }
    };

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, mb: 0 }}
            data-testid={`rule-group-${groupIndex}`}
        >
            {/* Group Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        Group {groupIndex + 1}
                    </Typography>
                    <Chip
                        label={group.logic || 'AND'}
                        size="small"
                        color={group.logic === 'OR' ? 'secondary' : 'primary'}
                        onClick={handleLogicToggle}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                        data-testid={`rule-group-logic-toggle-${groupIndex}`}
                    />
                </Box>
                <IconButton
                    size="small"
                    color="error"
                    onClick={handleDeleteGroup}
                    data-testid={`delete-rule-group-${groupIndex}`}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Conditions */}
            {(group.conditions || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    No conditions — add one below.
                </Typography>
            ) : (
                (group.conditions || []).map((cond, ci) => (
                    <ConditionRow
                        key={ci}
                        condition={cond}
                        groupIndex={groupIndex}
                        condIndex={ci}
                        onChange={(updated) => handleConditionChange(ci, updated)}
                        onDelete={() => handleDeleteCondition(ci)}
                        validationErrors={validationErrors}
                    />
                ))
            )}

            {/* Add Condition button */}
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddCondition}
                sx={{ mt: 1, textTransform: 'none' }}
                data-testid={`add-condition-${groupIndex}`}
            >
                Add Condition
            </Button>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Rule Group</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Delete this rule group and all its conditions?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => { setDeleteDialogOpen(false); onDelete(); }}
                        color="error"
                        variant="contained"
                        data-testid={`confirm-delete-rule-group-${groupIndex}`}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

// ─── AudienceRuleBuilder (main export) ───────────────────────────────────────

const AudienceRuleBuilder = ({ formData, setField, subscriptionOptions }) => {
    const [jsonViewOpen, setJsonViewOpen] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const audienceRules = useMemo(() => formData.audienceRules || [], [formData.audienceRules]);
    const subscriptionPlans = useMemo(() => formData.subscriptionPlans || ['all'], [formData.subscriptionPlans]);
    const isAllPlans = subscriptionPlans.includes('all');

    const validationErrors = useMemo(() => validateAllRules(audienceRules), [audienceRules]);
    const summaryText = useMemo(() => generateSummaryText(audienceRules), [audienceRules]);

    // ─── Subscription plan handlers ─────────────────────────────────────

    const handleAllPlansToggle = useCallback(() => {
        if (isAllPlans) {
            setField('subscriptionPlans', []);
        } else {
            setField('subscriptionPlans', ['all']);
        }
    }, [isAllPlans, setField]);

    const handlePlanToggle = useCallback((planId) => {
        const current = subscriptionPlans.filter(p => p !== 'all');
        const updated = current.includes(planId)
            ? current.filter(p => p !== planId)
            : [...current, planId];
        setField('subscriptionPlans', updated);
    }, [subscriptionPlans, setField]);

    // ─── Rule group handlers ────────────────────────────────────────────

    const handleAddRuleGroup = useCallback(() => {
        setField('audienceRules', [...audienceRules, { logic: 'AND', conditions: [] }]);
    }, [audienceRules, setField]);

    const handleRuleGroupChange = useCallback((groupIndex, updatedGroup) => {
        const newRules = [...audienceRules];
        newRules[groupIndex] = updatedGroup;
        setField('audienceRules', newRules);
    }, [audienceRules, setField]);

    const handleDeleteRuleGroup = useCallback((groupIndex) => {
        setField('audienceRules', audienceRules.filter((_, i) => i !== groupIndex));
    }, [audienceRules, setField]);

    // ─── JSON fallback handlers ─────────────────────────────────────────

    const handleJsonSave = useCallback((parsed) => {
        if (parsed && typeof parsed === 'object') {
            if (parsed.audienceRules !== undefined) {
                setField('audienceRules', parsed.audienceRules);
            }
            if (parsed.subscriptionPlans !== undefined) {
                setField('subscriptionPlans', parsed.subscriptionPlans);
            }
            // Also accept raw array (assume it's audienceRules)
            if (Array.isArray(parsed)) {
                setField('audienceRules', parsed);
            }
        }
    }, [setField]);

    // Expose validation for parent save logic
    const hasErrors = validationErrors.filter(e => e.severity !== 'warning').length > 0;

    return (
        <Paper sx={{ p: 3 }} data-testid="analysis-task-panel-audience">
            <Typography variant="h6" gutterBottom>Audience Targeting</Typography>

            {/* ─── AC1: Subscription Plans Filter ──────────────────────────── */}
            <Box sx={{ mb: 3 }} data-testid="subscription-plans-filter">
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Subscription Plans
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select which subscription plans should receive this analysis.
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={isAllPlans}
                            onChange={handleAllPlansToggle}
                            data-testid="subscription-plans-all-toggle"
                        />
                    }
                    label="All Plans"
                    sx={{ mb: 1 }}
                />

                <Collapse in={!isAllPlans}>
                    <FormGroup>
                        <Grid container>
                            {subscriptionOptions.map((plan) => (
                                <Grid item xs={12} sm={6} md={4} key={plan.id} data-testid={`subscription-plan-option-${plan.id}`}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={subscriptionPlans.filter(p => p !== 'all').includes(plan.id)}
                                                onChange={() => handlePlanToggle(plan.id)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2">{plan.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {plan.id}{plan.price ? ` — ${plan.price} ${plan.currency || ''}/${plan.interval || 'month'}` : ''}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            ))}
                            {subscriptionOptions.length === 0 && (
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        No subscription plans found. Toggle &quot;All Plans&quot; on to target all users.
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </FormGroup>
                </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ─── AC2 + AC3: Targeting Rules ──────────────────────────────── */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Targeting Rules
                </Typography>

                {audienceRules.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }} data-testid="no-rules-message">
                        No targeting rules — this task will run for all users matching the subscription filter.
                    </Alert>
                ) : (
                    audienceRules.map((group, gi) => (
                        <Box key={gi}>
                            {gi > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
                                    <Chip
                                        label="AND"
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 700, letterSpacing: 1 }}
                                    />
                                </Box>
                            )}
                            <RuleGroup
                                group={group}
                                groupIndex={gi}
                                onChange={(updated) => handleRuleGroupChange(gi, updated)}
                                onDelete={() => handleDeleteRuleGroup(gi)}
                                validationErrors={validationErrors}
                            />
                        </Box>
                    ))
                )}

                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddRuleGroup}
                    sx={{ mt: 2, textTransform: 'none' }}
                    data-testid="add-rule-group"
                >
                    Add Rule Group
                </Button>
            </Box>

            {/* ─── AC5: Validation Errors (shown on demand) ────────────────── */}
            {validationErrors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        size="small"
                        onClick={() => setShowValidation(v => !v)}
                        sx={{ textTransform: 'none', color: hasErrors ? 'error.main' : 'warning.main' }}
                    >
                        {hasErrors ? `${validationErrors.filter(e => e.severity !== 'warning').length} validation error(s)` : `${validationErrors.length} warning(s)`}
                        {showValidation ? <ExpandLessIcon sx={{ ml: 0.5 }} fontSize="small" /> : <ExpandMoreIcon sx={{ ml: 0.5 }} fontSize="small" />}
                    </Button>
                    <Collapse in={showValidation}>
                        <Box sx={{ mt: 1 }}>
                            {validationErrors.map((err) => (
                                <Alert
                                    key={err.key}
                                    severity={err.severity === 'warning' ? 'warning' : 'error'}
                                    sx={{ mb: 0.5, py: 0 }}
                                >
                                    {err.message}
                                </Alert>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ─── AC6: Rule Summary ───────────────────────────────────────── */}
            <Box sx={{ mb: 3 }} data-testid="audience-summary">
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Rule Summary
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ fontStyle: audienceRules.length === 0 ? 'italic' : 'normal' }}>
                        {summaryText}
                    </Typography>
                </Paper>
            </Box>

            {/* ─── AC7: JSON Fallback ──────────────────────────────────────── */}
            <Box data-testid="audience-json-toggle">
                <Button
                    size="small"
                    onClick={() => setJsonViewOpen(v => !v)}
                    startIcon={jsonViewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ textTransform: 'none', color: 'text.secondary', mb: 1 }}
                >
                    Advanced: JSON View
                </Button>
                <Collapse in={jsonViewOpen}>
                    <JsonViewTab
                        data={{ audienceRules, subscriptionPlans }}
                        onSave={handleJsonSave}
                    />
                </Collapse>
            </Box>
        </Paper>
    );
};

// Export for testing
export { FIELD_METADATA, CATEGORY_LABELS, CATEGORY_ORDER, OPERATOR_LABELS, getOperatorsForField, generateSummaryText, validateAllRules };
export default AudienceRuleBuilder;
